import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Plus, X, Phone, GlobeHemisphereWest, Code, Copy, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const SOURCES = [
  { id: "organic", label: "Organic Google", color: "#22C55E" },
  { id: "gbp", label: "Google Business Profile", color: "#002FA7" },
  { id: "paid", label: "Paid Ads", color: "#FF3B30" },
  { id: "social", label: "Social", color: "#A855F7" },
  { id: "direct", label: "Direct", color: "#71717A" },
  { id: "referral", label: "Referral", color: "#EAB308" },
];

export default function Attribution() {
  const { activeBiz } = useOutletContext();
  const [tab, setTab] = useState("dashboard");
  const [numbers, setNumbers] = useState([]);
  const [calls, setCalls] = useState([]);
  const [visits, setVisits] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pixel, setPixel] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ source: "organic", phone: "", label: "" });

  const reload = async () => {
    if (!activeBiz) return;
    try {
      const [n, c, v, s, p] = await Promise.all([
        api.get(`/businesses/${activeBiz.id}/attribution/numbers`),
        api.get(`/businesses/${activeBiz.id}/attribution/calls`),
        api.get(`/businesses/${activeBiz.id}/attribution/visits`),
        api.get(`/businesses/${activeBiz.id}/attribution/summary`),
        api.get(`/businesses/${activeBiz.id}/pixel`),
      ]);
      setNumbers(n.data); setCalls(c.data); setVisits(v.data);
      setSummary(s.data); setPixel(p.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeBiz]);

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;

  const addNum = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/businesses/${activeBiz.id}/attribution/numbers`, form);
      setForm({ source: "organic", phone: "", label: "" });
      setShowAdd(false);
      reload();
      toast.success("Tracking number added");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const removeNum = async (id) => { try { await api.delete(`/businesses/${activeBiz.id}/attribution/numbers/${id}`); reload(); } catch (e) { toast.error(formatErr(e)); } };

  const simulateCall = async (source) => {
    try {
      await api.post(`/businesses/${activeBiz.id}/attribution/call/simulate`, {
        business_id: activeBiz.id, source, from_number: "+15555550" + Math.floor(Math.random() * 900 + 100),
        duration_sec: Math.floor(Math.random() * 300) + 30, answered: true,
      });
      toast.success(`Simulated ${source} call`);
      reload();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-6xl">
      <header>
        <div className="label-tiny mb-1">Attribution</div>
        <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="attr-title">UTM Calls + Site Visits</h1>
        <p className="text-zinc-500 mt-1">Real-provider grade attribution: every call and visit tagged to its source. Prove ROI per channel.</p>
      </header>

      <div className="flex gap-px bg-zinc-200 border border-zinc-200 w-fit" data-testid="attr-tabs">
        {[
          { id: "dashboard", label: "Dashboard" },
          { id: "numbers", label: "Tracking Numbers" },
          { id: "calls", label: "Calls" },
          { id: "visits", label: "Visits" },
          { id: "snippets", label: "Embed snippets" },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2.5 text-xs uppercase tracking-widest font-bold ${tab === t.id ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"}`} data-testid={`attr-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {tab === "dashboard" && summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Visits (7d)" value={summary.total_visits} testid="attr-stat-visits" />
            <Stat label="Calls (7d)" value={summary.total_calls} testid="attr-stat-calls" />
            <Stat label="Leads (7d)" value={summary.total_leads} testid="attr-stat-leads" />
          </div>
          <div className="bento p-6 space-y-3">
            <h3 className="font-display text-xl font-bold">By traffic source</h3>
            {SOURCES.map((s) => {
              const row = summary.by_source[s.id] || { visits: 0, calls: 0, leads: 0 };
              const max = Math.max(...Object.values(summary.by_source).map((v) => v.visits || 0), 1);
              return (
                <div key={s.id} className="flex items-center gap-3 text-sm" data-testid={`attr-row-${s.id}`}>
                  <span className="w-44 font-mono text-xs label-tiny" style={{ color: s.color }}>{s.label}</span>
                  <div className="flex-1 h-6 bg-zinc-100 relative">
                    <div className="absolute top-0 left-0 h-full" style={{ width: `${(row.visits / max) * 100}%`, background: s.color }}></div>
                  </div>
                  <span className="w-32 font-mono text-xs text-zinc-600 text-right">{row.visits}v · {row.calls}c · {row.leads}l</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "numbers" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-zinc-600">Add a phone number per traffic source. The DNI snippet (Embed snippets tab) swaps numbers on your site based on referrer/UTM.</p>
            <div className="flex gap-2">
              <button className="btn-dark !py-2 !text-[11px]" onClick={() => setShowAdd(!showAdd)} data-testid="attr-add-toggle"><Plus size={12} weight="bold" /> Add number</button>
              <button className="btn-secondary !py-2 !text-[11px]" onClick={reload} data-testid="attr-refresh"><ArrowsClockwise size={12} /> Refresh</button>
            </div>
          </div>
          {showAdd && (
            <form onSubmit={addNum} className="bento p-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="attr-add-form">
              <select className="input-field" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                {SOURCES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <input className="input-field" placeholder="+15551234567" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="attr-phone" />
              <input className="input-field" placeholder="Label (optional)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
              <button className="btn-primary justify-center" type="submit" data-testid="attr-add-submit">Add</button>
            </form>
          )}
          <div className="bento p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-zinc-50 border-b border-zinc-200"><th className="label-tiny p-3 text-left">Source</th><th className="label-tiny p-3 text-left">Phone</th><th className="label-tiny p-3 text-left">Label</th><th className="p-3"></th></tr></thead>
              <tbody>
                {numbers.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-zinc-400">No tracking numbers yet</td></tr>}
                {numbers.map((n) => (
                  <tr key={n.id} className="border-b border-zinc-100" data-testid={`attr-num-${n.id}`}>
                    <td className="p-3"><span className="label-tiny">{n.source}</span></td>
                    <td className="p-3 font-mono">{n.phone}</td>
                    <td className="p-3">{n.label}</td>
                    <td className="p-3 text-right"><button onClick={() => removeNum(n.id)} className="text-zinc-400 hover:text-zinc-900"><X size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bento p-4 bg-yellow-50 border-l-4 border-[#FACC15] text-sm font-mono">
            <strong>Quick test:</strong> simulate a call from each source →{" "}
            {SOURCES.map((s) => (
              <button key={s.id} onClick={() => simulateCall(s.id)} className="ml-2 underline" data-testid={`attr-sim-${s.id}`}>{s.id}</button>
            ))}
          </div>
        </div>
      )}

      {tab === "calls" && (
        <div className="bento p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-zinc-50 border-b border-zinc-200"><th className="label-tiny p-3 text-left">Source</th><th className="label-tiny p-3 text-left">From</th><th className="label-tiny p-3 text-left">To</th><th className="label-tiny p-3 text-left">Duration</th><th className="label-tiny p-3 text-left">UTM</th><th className="label-tiny p-3 text-left">When</th></tr></thead>
            <tbody>
              {calls.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-zinc-400">No calls yet</td></tr>}
              {calls.map((c) => (
                <tr key={c.id} className="border-b border-zinc-100" data-testid={`attr-call-${c.id}`}>
                  <td className="p-3"><span className="label-tiny">{c.source}</span></td>
                  <td className="p-3 font-mono text-xs">{c.from_number}</td>
                  <td className="p-3 font-mono text-xs">{c.to_number || "—"}</td>
                  <td className="p-3 font-mono text-xs">{Math.floor(c.duration_sec / 60)}m {c.duration_sec % 60}s</td>
                  <td className="p-3 font-mono text-xs text-zinc-500">{c.utm_source || "—"}/{c.utm_medium || "—"}/{c.utm_campaign || "—"}</td>
                  <td className="p-3 font-mono text-xs text-zinc-500">{new Date(c.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "visits" && (
        <div className="bento p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-zinc-50 border-b border-zinc-200"><th className="label-tiny p-3 text-left">Source</th><th className="label-tiny p-3 text-left">Path</th><th className="label-tiny p-3 text-left">Referrer</th><th className="label-tiny p-3 text-left">UTM</th><th className="label-tiny p-3 text-left">When</th></tr></thead>
            <tbody>
              {visits.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-zinc-400">No visits yet — install the pixel below</td></tr>}
              {visits.map((v) => (
                <tr key={v.id} className="border-b border-zinc-100" data-testid={`attr-visit-${v.id}`}>
                  <td className="p-3"><span className="label-tiny">{v.source}</span></td>
                  <td className="p-3 font-mono text-xs">{v.path}</td>
                  <td className="p-3 font-mono text-xs truncate max-w-[200px]">{v.referrer || "—"}</td>
                  <td className="p-3 font-mono text-xs text-zinc-500">{v.utm_source || "—"}/{v.utm_medium || "—"}</td>
                  <td className="p-3 font-mono text-xs text-zinc-500">{new Date(v.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "snippets" && pixel && (
        <div className="space-y-4">
          <div className="bento p-6 space-y-3">
            <h3 className="font-display text-lg font-bold flex items-center gap-2"><GlobeHemisphereWest size={18} /> Visit Pixel</h3>
            <p className="text-sm text-zinc-600">Paste before <code className="font-mono">&lt;/body&gt;</code> on every page of the website.</p>
            <pre className="bg-zinc-950 text-[#FACC15] text-xs p-4 font-mono whitespace-pre-wrap break-all" data-testid="attr-pixel">{pixel.visit_pixel}</pre>
            <button className="btn-dark !py-2 !text-[11px]" onClick={() => { navigator.clipboard.writeText(pixel.visit_pixel); toast.success("Copied"); }} data-testid="attr-pixel-copy"><Copy size={12} weight="bold" /> Copy pixel</button>
          </div>
          <div className="bento p-6 space-y-3">
            <h3 className="font-display text-lg font-bold flex items-center gap-2"><Phone size={18} /> Dynamic Number Insertion</h3>
            <p className="text-sm text-zinc-600">Mark any phone link/element on your site with <code className="font-mono">data-track-phone</code>. The script swaps in the right tracking number based on traffic source.</p>
            <pre className="bg-zinc-950 text-[#22C55E] text-xs p-4 font-mono whitespace-pre-wrap break-all" data-testid="attr-dni">{pixel.dni_snippet}</pre>
            <div className="bg-zinc-50 p-3 font-mono text-xs">
              Example markup: <code>&lt;a data-track-phone href="tel:+15555550100"&gt;+1 (555) 555-0100&lt;/a&gt;</code>
            </div>
            <button className="btn-dark !py-2 !text-[11px]" onClick={() => { navigator.clipboard.writeText(pixel.dni_snippet); toast.success("Copied"); }} data-testid="attr-dni-copy"><Copy size={12} weight="bold" /> Copy DNI</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bento p-4" data-testid={testid}>
      <div className="label-tiny">{label}</div>
      <div className="font-display text-3xl font-black mt-1 tabular-nums">{value}</div>
    </div>
  );
}
